import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Copy,
  Eye,
  FilePlus2,
  Lock,
  Pencil,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CILT_LABELS,
  PERMISSIONS,
  type CiltType,
  type TemplateVersionStatus,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  Textarea,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useCan } from '../../lib/auth';
import { formatDateTime, humanise } from '../../lib/format';
import { EMPTY_ITEM, ItemFormModal, type ItemDraft } from './ItemFormModal';
import { TemplatePreview } from './TemplatePreview';

interface VersionContent {
  version: {
    id: string;
    versionNo: number;
    status: TemplateVersionStatus;
    changeNote: string | null;
    publishedAt: string | null;
    approvedAt: string | null;
    submittedAt: string | null;
    rejectionReason: string | null;
  };
  sections: Array<{
    id: string;
    code: string | null;
    title: string;
    description: string | null;
    sortOrder: number;
    items: Array<ItemDraft & { id: string; engineeringStandardId: string | null }>;
  }>;
}

interface TemplateResponse {
  template: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    frequency: string;
    machineType: string | null;
    isActive: boolean;
    currentVersionId: string | null;
  };
  content: VersionContent | null;
}

interface Validation {
  ok: boolean;
  errors: Array<{ scope: string; targetId: string | null; label: string; message: string }>;
  warnings: Array<{ scope: string; targetId: string | null; label: string; message: string }>;
}

const STATUS_TONE: Record<TemplateVersionStatus, 'ok' | 'info' | 'warn' | 'neutral'> = {
  DRAFT: 'warn',
  IN_REVIEW: 'info',
  APPROVED: 'info',
  PUBLISHED: 'ok',
  SUPERSEDED: 'neutral',
};

const TRANSITION_LABEL: Record<string, string> = {
  IN_REVIEW: 'Submit for review',
  APPROVED: 'Approve',
  DRAFT: 'Send back to draft',
  SUPERSEDED: 'Supersede',
};

/**
 * Checklist template editor.
 *
 * Desktop/tablet workflow deliberately — this is an AM Coach sitting down to
 * build a checklist, not an operator at a machine. The execution screen stays
 * mobile-first.
 */
export function TemplateEditorPage() {
  const { templateId = '' } = useParams();
  const queryClient = useQueryClient();

  const canManage = useCan(PERMISSIONS.TEMPLATE_MANAGE);
  const canApprove = useCan(PERMISSIONS.TEMPLATE_APPROVE);
  const canReview = useCan(PERMISSIONS.TEMPLATE_REVIEW);
  const canPublish = useCan(PERMISSIONS.TEMPLATE_PUBLISH);

  const [error, setError] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [editingItem, setEditingItem] = useState<{ sectionId: string; draft: ItemDraft } | null>(null);
  const [editingSection, setEditingSection] = useState<{ id?: string; title: string; description: string } | null>(null);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => api.get<TemplateResponse>(`/api/checklist-templates/${templateId}`),
  });

  const { data: templateList } = useQuery({
    queryKey: ['templates', 'list'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          versions: Array<{ id: string; versionNo: number; status: TemplateVersionStatus }>;
        }>;
      }>('/api/checklist-templates?includeInactive=true'),
  });

  const versions = templateList?.data.find((t) => t.id === templateId)?.versions ?? [];
  const activeVersionId =
    selectedVersionId ||
    versions.find((v) => v.status !== 'PUBLISHED' && v.status !== 'SUPERSEDED')?.id ||
    data?.template.currentVersionId ||
    '';

  const { data: viewed } = useQuery({
    queryKey: ['template-version', activeVersionId],
    queryFn: () => api.get<VersionContent>(`/api/checklist-templates/versions/${activeVersionId}`),
    enabled: Boolean(activeVersionId),
  });

  const { data: validation } = useQuery({
    queryKey: ['template-validate', activeVersionId],
    queryFn: () => api.get<Validation>(`/api/checklist-templates/versions/${activeVersionId}/validate`),
    enabled: Boolean(activeVersionId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['template'] });
    void queryClient.invalidateQueries({ queryKey: ['template-version'] });
    void queryClient.invalidateQueries({ queryKey: ['template-validate'] });
    void queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  /**
   * Every editor action goes through one mutation.
   *
   * why: a helper that called useMutation per action would be calling a hook
   * from a plain function — it happens to work while the call order is stable,
   * but it is exactly the pattern that breaks the first time someone makes one
   * of them conditional. One hook, dispatched on `kind`, cannot break that way.
   */
  type EditorAction =
    | { kind: 'section.save'; id?: string; title: string; description: string }
    | { kind: 'section.delete'; id: string }
    | { kind: 'section.reorder'; sectionIds: string[] }
    | { kind: 'item.save'; sectionId: string; draft: ItemDraft }
    | { kind: 'item.delete'; id: string }
    | { kind: 'item.duplicate'; id: string }
    | { kind: 'item.reorder'; itemIds: string[] }
    | { kind: 'version.transition'; to: TemplateVersionStatus }
    | { kind: 'version.publish' }
    | { kind: 'version.createDraft'; changeNote: string };

  const action = useMutation({
    mutationFn: async (input: EditorAction) => {
      switch (input.kind) {
        case 'section.save':
          return input.id
            ? api.patch(`/api/checklist-templates/sections/${input.id}`, {
                title: input.title,
                description: input.description || null,
              })
            : api.post(`/api/checklist-templates/versions/${activeVersionId}/sections`, {
                title: input.title,
                description: input.description || null,
                sortOrder: viewed?.sections.length ?? 0,
              });
        case 'section.delete':
          return api.delete(`/api/checklist-templates/sections/${input.id}`);
        case 'section.reorder':
          return api.put(
            `/api/checklist-templates/versions/${activeVersionId}/section-order`,
            { sectionIds: input.sectionIds },
          );
        case 'item.save': {
          const body = itemBody(input.draft);
          return input.draft.id
            ? api.patch(`/api/checklist-templates/items/${input.draft.id}`, body)
            : api.post(`/api/checklist-templates/sections/${input.sectionId}/items`, {
                ...body,
                seq: input.draft.seq,
              });
        }
        case 'item.delete':
          return api.delete(`/api/checklist-templates/items/${input.id}`);
        case 'item.duplicate':
          return api.post(`/api/checklist-templates/items/${input.id}/duplicate`, {});
        case 'item.reorder':
          return api.put(`/api/checklist-templates/versions/${activeVersionId}/item-order`, {
            itemIds: input.itemIds,
          });
        case 'version.transition':
          return api.post(`/api/checklist-templates/versions/${activeVersionId}/transition`, {
            to: input.to,
          });
        case 'version.publish':
          return api.post(`/api/checklist-templates/versions/${activeVersionId}/publish`);
        case 'version.createDraft':
          return api.post(`/api/checklist-templates/${templateId}/versions`, {
            changeNote: input.changeNote,
          });
      }
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That change could not be saved.'),
  });

  const busyWith = (kind: EditorAction['kind']) =>
    action.isPending && action.variables?.kind === kind;

  if (isLoading || !data) return <Spinner label="Loading checklist" />;

  const content = viewed ?? data.content;
  const status = content?.version.status;
  const editable = status === 'DRAFT' && canManage;
  const readOnly = Boolean(status) && !editable;

  const allItemIds = content?.sections.flatMap((s) => s.items.map((i) => i.id)) ?? [];

  const moveItem = (itemId: string, direction: -1 | 1) => {
    const index = allItemIds.indexOf(itemId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= allItemIds.length) return;
    const next = [...allItemIds];
    [next[index], next[target]] = [next[target]!, next[index]!];
    action.mutate({ kind: 'item.reorder', itemIds: next });
  };

  const moveSection = (sectionId: string, direction: -1 | 1) => {
    const ids = content?.sections.map((s) => s.id) ?? [];
    const index = ids.indexOf(sectionId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[index], next[target]] = [next[target]!, next[index]!];
    action.mutate({ kind: 'section.reorder', sectionIds: next });
  };

  const issuesFor = (targetId: string) => ({
    errors: validation?.errors.filter((e) => e.targetId === targetId) ?? [],
    warnings: validation?.warnings.filter((w) => w.targetId === targetId) ?? [],
  });

  const allowedTransitions: TemplateVersionStatus[] =
    status === 'DRAFT' ? ['IN_REVIEW'] : status === 'IN_REVIEW' ? ['APPROVED', 'DRAFT'] : status === 'APPROVED' ? ['DRAFT'] : [];

  const canDo = (to: TemplateVersionStatus) =>
    to === 'APPROVED' ? canApprove : to === 'DRAFT' ? canReview : canManage;

  return (
    <div>
      <PageHeader
        title={`${data.template.code} — ${data.template.name}`}
        back={{ to: '/admin/checklists', label: 'All checklists' }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{humanise(data.template.frequency)}</Badge>
            {data.template.machineType ? <Badge>{data.template.machineType}</Badge> : null}
            {content ? (
              <>
                <span>Version {content.version.versionNo}</span>
                <StatusPill tone={STATUS_TONE[content.version.status]} label={humanise(content.version.status)} />
              </>
            ) : null}
          </span>
        }
        actions={
          <>
            {versions.length > 1 ? (
              <Select
                value={activeVersionId}
                onChange={(event) => setSelectedVersionId(event.target.value)}
                aria-label="Version"
                className="w-44"
              >
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    v{version.versionNo} — {humanise(version.status)}
                  </option>
                ))}
              </Select>
            ) : null}

            <Button variant="secondary" icon={Eye} onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>

            {allowedTransitions.map((to) =>
              canDo(to) ? (
                <Button
                  key={to}
                  variant={to === 'APPROVED' ? 'primary' : 'secondary'}
                  icon={to === 'IN_REVIEW' ? Send : CheckCircle2}
                  loading={busyWith('version.transition') && action.variables?.kind === 'version.transition' && action.variables.to === to}
                  onClick={() => action.mutate({ kind: 'version.transition', to })}
                >
                  {TRANSITION_LABEL[to]}
                </Button>
              ) : null,
            )}

            {status === 'APPROVED' && canPublish ? (
              <Button
                icon={Send}
                loading={busyWith('version.publish')}
                onClick={() => action.mutate({ kind: 'version.publish' })}
              >
                Publish v{content?.version.versionNo}
              </Button>
            ) : null}

            {canManage && (status === 'PUBLISHED' || status === 'SUPERSEDED') ? (
              <Button variant="secondary" icon={FilePlus2} onClick={() => setNewVersionOpen(true)}>
                New version
              </Button>
            ) : null}
          </>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        {status === 'PUBLISHED' ? (
          <Callout tone="ok" title="Published — read only">
            Published {formatDateTime(content?.version.publishedAt)}. Operators are performing this
            version now, and completed runs stay linked to it forever. To change anything, start a
            new version.
          </Callout>
        ) : null}

        {status === 'SUPERSEDED' ? (
          <Callout tone="neutral" title="Superseded — read only">
            A newer version has taken over. This one is kept because historical runs reference it.
          </Callout>
        ) : null}

        {status === 'IN_REVIEW' ? (
          <Callout tone="info" title="In review — content is frozen">
            Send it back to draft to make further changes.
          </Callout>
        ) : null}

        {status === 'APPROVED' ? (
          <Callout tone="info" title="Approved — ready to publish">
            Approved {formatDateTime(content?.version.approvedAt)}.
          </Callout>
        ) : null}

        {content?.version.rejectionReason ? (
          <Callout tone="warn" title="Sent back for changes">
            {content.version.rejectionReason}
          </Callout>
        ) : null}

        {/* Validation report */}
        {validation && editable ? (
          validation.errors.length > 0 || validation.warnings.length > 0 ? (
            <Card>
              <CardHeader
                title="Before this can be published"
                subtitle={`${validation.errors.length} error${validation.errors.length === 1 ? '' : 's'}, ${validation.warnings.length} warning${validation.warnings.length === 1 ? '' : 's'}`}
              />
              <ul className="divide-y divide-steel-100">
                {[...validation.errors, ...validation.warnings].map((issue, index) => (
                  <li key={index} className="flex items-start gap-3 px-4 py-2.5">
                    <AlertTriangle
                      aria-hidden
                      className={clsx(
                        'mt-0.5 size-4 shrink-0',
                        validation.errors.includes(issue) ? 'text-alarm-600' : 'text-warn-600',
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-steel-900">{issue.label}</p>
                      <p className="text-sm text-steel-600">{issue.message}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ) : (
            <Callout tone="ok" title="No problems found">
              This checklist is ready to be submitted for review.
            </Callout>
          )
        ) : null}

        {/* Sections */}
        {!content || content.sections.length === 0 ? (
          <Card>
            <EmptyState
              icon={Plus}
              title="No sections yet"
              message="Checklists are organised into sections — Pre-Start, During Production, End of Shift, or by CILT activity."
              action={
                editable ? (
                  <Button
                    icon={Plus}
                    onClick={() => setEditingSection({ title: '', description: '' })}
                  >
                    Add the first section
                  </Button>
                ) : null
              }
            />
          </Card>
        ) : (
          content.sections.map((section, sectionIndex) => {
            const sectionIssues = issuesFor(section.id);
            return (
              <Card key={section.id}>
                <div className="flex items-start justify-between gap-3 border-b border-steel-200 px-4 py-3">
                  <div className="min-w-0">
                    <h2 className="text-sm font-bold text-steel-900">{section.title}</h2>
                    {section.description ? (
                      <p className="mt-0.5 text-xs text-steel-500">{section.description}</p>
                    ) : null}
                    <p className="mt-0.5 text-xs text-steel-400">
                      {section.items.length} task{section.items.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  {editable ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={ArrowUp}
                        aria-label="Move section up"
                        disabled={sectionIndex === 0}
                        onClick={() => moveSection(section.id, -1)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={ArrowDown}
                        aria-label="Move section down"
                        disabled={sectionIndex === content.sections.length - 1}
                        onClick={() => moveSection(section.id, 1)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Pencil}
                        aria-label="Edit section"
                        onClick={() =>
                          setEditingSection({
                            id: section.id,
                            title: section.title,
                            description: section.description ?? '',
                          })
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        aria-label="Delete section"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete "${section.title}" and its ${section.items.length} task(s)? This only affects this draft.`,
                            )
                          ) {
                            action.mutate({ kind: 'section.delete', id: section.id });
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </div>

                {sectionIssues.errors.length > 0 ? (
                  <div className="border-b border-steel-100 bg-alarm-50 px-4 py-2 text-xs font-semibold text-alarm-700">
                    {sectionIssues.errors.map((e) => e.message).join(' ')}
                  </div>
                ) : null}

                <ul className="divide-y divide-steel-100">
                  {section.items.map((item) => {
                    const itemIssues = issuesFor(item.id);
                    const measured = item.itemType === 'NUMERIC' || item.itemType === 'COUNTER';
                    const globalIndex = allItemIds.indexOf(item.id);

                    return (
                      <li key={item.id} className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          <span className="w-7 shrink-0 text-sm font-bold text-steel-400 tabular-nums">
                            {item.seq}
                          </span>

                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-steel-900">{item.task}</p>

                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <Badge>{CILT_LABELS[item.ciltType as CiltType] ?? item.ciltType}</Badge>
                              <Badge>{humanise(item.itemType)}</Badge>
                              {item.isSafetyCritical ? (
                                <Badge tone="alarm" icon={Lock}>
                                  Safety critical
                                </Badge>
                              ) : null}
                              {!item.isRequired ? <Badge>Optional</Badge> : null}
                              {item.failureBehaviour !== 'NONE' ? (
                                <Badge tone="warn">On fail: {humanise(item.failureBehaviour)}</Badge>
                              ) : null}
                              {measured ? (
                                item.recordOnly ? (
                                  <Badge tone="info">Recorded — not scored</Badge>
                                ) : (
                                  <Badge tone="ok">Scored</Badge>
                                )
                              ) : null}
                            </div>

                            {item.standard ? (
                              <p className="mt-1 text-xs text-steel-600">
                                <span className="font-semibold">Standard:</span> {item.standard}
                              </p>
                            ) : null}

                            {measured ? (
                              <p className="mt-1 text-xs font-semibold text-info-700">
                                {item.recordOnly
                                  ? `Target ${item.targetValue ?? '—'}${item.unit ? ` ${item.unit}` : ''} — reading recorded, not scored`
                                  : `Acceptable: ${item.minValue ?? '−∞'} to ${item.maxValue ?? '∞'}${item.unit ? ` ${item.unit}` : ''}`}
                                {item.engineeringStandardId ? ' · linked to an engineering standard' : ''}
                              </p>
                            ) : null}

                            {itemIssues.errors.map((issue, index) => (
                              <p
                                key={`e${index}`}
                                className="mt-1 rounded bg-alarm-50 px-2 py-1 text-xs font-semibold text-alarm-700"
                              >
                                {issue.message}
                              </p>
                            ))}
                            {itemIssues.warnings.map((issue, index) => (
                              <p
                                key={`w${index}`}
                                className="mt-1 rounded bg-warn-50 px-2 py-1 text-xs font-semibold text-warn-700"
                              >
                                {issue.message}
                              </p>
                            ))}
                          </div>

                          {editable ? (
                            <div className="flex shrink-0 items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={ArrowUp}
                                aria-label={`Move task ${item.seq} up`}
                                disabled={globalIndex === 0}
                                onClick={() => moveItem(item.id, -1)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={ArrowDown}
                                aria-label={`Move task ${item.seq} down`}
                                disabled={globalIndex === allItemIds.length - 1}
                                onClick={() => moveItem(item.id, 1)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={Copy}
                                aria-label={`Duplicate task ${item.seq}`}
                                onClick={() => action.mutate({ kind: 'item.duplicate', id: item.id })}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={Pencil}
                                aria-label={`Edit task ${item.seq}`}
                                onClick={() =>
                                  setEditingItem({
                                    sectionId: section.id,
                                    draft: { ...item, options: item.options ?? [] },
                                  })
                                }
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={Trash2}
                                aria-label={`Delete task ${item.seq}`}
                                onClick={() => {
                                  if (window.confirm(`Delete task "${item.task}"?`)) {
                                    action.mutate({ kind: 'item.delete', id: item.id });
                                  }
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {editable ? (
                  <div className="border-t border-steel-100 p-3">
                    <Button
                      variant="secondary"
                      icon={Plus}
                      onClick={() =>
                        setEditingItem({
                          sectionId: section.id,
                          draft: {
                            ...EMPTY_ITEM,
                            seq: (allItemIds.length ?? 0) + 1,
                          },
                        })
                      }
                    >
                      Add a task to {section.title}
                    </Button>
                  </div>
                ) : null}
              </Card>
            );
          })
        )}

        {editable && content && content.sections.length > 0 ? (
          <Button
            variant="secondary"
            icon={Plus}
            onClick={() => setEditingSection({ title: '', description: '' })}
          >
            Add another section
          </Button>
        ) : null}
      </div>

      {editingItem ? (
        <ItemFormModal
          initial={editingItem.draft}
          machineType={data.template.machineType}
          busy={busyWith('item.save')}
          serverError={error}
          onClose={() => setEditingItem(null)}
          onSubmit={(draft) => {
            action.mutate(
              { kind: 'item.save', sectionId: editingItem.sectionId, draft },
              { onSuccess: () => setEditingItem(null) },
            );
          }}
        />
      ) : null}

      {editingSection ? (
        <SectionModal
          initial={editingSection}
          busy={busyWith('section.save')}
          onClose={() => setEditingSection(null)}
          onSubmit={(input) =>
            action.mutate(
              { kind: 'section.save', ...input },
              { onSuccess: () => setEditingSection(null) },
            )
          }
        />
      ) : null}

      {newVersionOpen ? (
        <NewVersionModal
          busy={busyWith('version.createDraft')}
          onClose={() => setNewVersionOpen(false)}
          onSubmit={(note) =>
            action.mutate(
              { kind: 'version.createDraft', changeNote: note },
              {
                onSuccess: () => {
                  setNewVersionOpen(false);
                  setSelectedVersionId('');
                },
              },
            )
          }
        />
      ) : null}

      {previewOpen && content ? (
        <TemplatePreview
          templateName={data.template.name}
          content={content}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** The wire shape for an item, shared by create and update. */
function itemBody(draft: ItemDraft) {
  return {
    task: draft.task,
    itemType: draft.itemType,
    ciltType: draft.ciltType,
    componentId: draft.componentId,
    toolMethod: draft.toolMethod,
    standard: draft.standard,
    instruction: draft.instruction,
    isRequired: draft.isRequired,
    expectedValue: draft.expectedValue,
    minValue: draft.minValue,
    maxValue: draft.maxValue,
    targetValue: draft.targetValue,
    tolerance: draft.tolerance,
    recordOnly: draft.recordOnly,
    unit: draft.unit,
    failureBehaviour: draft.failureBehaviour,
    requiresPhoto: draft.requiresPhoto,
    requiresPhotoOnFail: draft.requiresPhotoOnFail,
    safetyWarning: draft.safetyWarning,
    qualityWarning: draft.qualityWarning,
    isSafetyCritical: draft.isSafetyCritical,
    defaultSeverity: draft.defaultSeverity,
    oplId: draft.oplId,
    engineeringStandardId: draft.engineeringStandardId,
    options: draft.options,
  };
}

function SectionModal({
  initial,
  busy,
  onClose,
  onSubmit,
}: {
  initial: { id?: string; title: string; description: string };
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { id?: string; title: string; description: string }) => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [description, setDescription] = useState(initial.description);

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.id ? 'Edit section' : 'Add a section'}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={!title.trim()}
            onClick={() => onSubmit({ id: initial.id, title: title.trim(), description })}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Section name" required hint="e.g. Pre-Start Checks, Cleaning, Tightening">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <Field label="When it happens" hint="Optional. e.g. Before production, Every 2 hours">
          <Input value={description} onChange={(event) => setDescription(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function NewVersionModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (changeNote: string) => void;
}) {
  const [note, setNote] = useState('');

  return (
    <Modal
      open
      onClose={onClose}
      title="Start a new version"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={note.trim().length < 3}
            icon={CheckCircle2}
            onClick={() => onSubmit(note.trim())}
          >
            Create draft
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Callout tone="info">
          The new version starts as a copy of the published one. Operators keep using the published
          version until this draft is reviewed, approved and published. Nothing already recorded
          changes.
        </Callout>
        <Field label="What is changing and why?" required>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            placeholder="e.g. Add a monthly air-filter check after PT-00031."
          />
        </Field>
      </div>
    </Modal>
  );
}
